import { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { useSession } from '../context/SessionContext';

export default function useAchievementController() {
  const { session } = useSession();
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAchievements = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('achievement')
        .select('*')
        .eq('user_id', session.user.id)
        .order('date', { ascending: false });

      if (error) throw error;

      const withImages = await Promise.all(
        data.map(async (ach) => {
          const { data: images } = await supabase
            .from('image')
            .select('*')
            .eq('category_id', ach.id)
            .eq('category', 'achievement');

          const imagesWithUrl = images?.map(img => ({
            ...img,
            publicUrl: supabase.storage.from('document').getPublicUrl(img.path).data.publicUrl
          })) || [];

          return { ...ach, images: imagesWithUrl };
        })
      );

      setAchievements(withImages);
    } catch (err) {
      console.error('Error fetching achievements:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const addAchievement = async (data, images) => {
    try {
      setLoading(true);
      const { data: newAch, error } = await supabase
        .from('achievement')
        .insert([{ ...data, user_id: session.user.id }])
        .select()
        .single();

      if (error) throw error;

      if (images?.length > 0) {
        await Promise.all(images.map(async (img) => {
          if (!(img instanceof File)) return;

          const fileExt = img.name.split('.').pop();
          const fileName = `${Math.random()}.${fileExt}`;
          
          const { error: uploadError } = await supabase.storage
            .from('document')
            .upload(fileName, img);

          if (uploadError) throw uploadError;

          await supabase.from('image').insert({
            user_id: session.user.id,
            category: 'achievement',
            category_id: newAch.id,
            path: fileName
          });
        }));
      }

      await fetchAchievements();
    } catch (err) {
      console.error('Error adding achievement:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateAchievement = async (id, data, images) => {
    try {
      setLoading(true);
      const { error } = await supabase
        .from('achievement')
        .update(data)
        .eq('id', id);

      if (error) throw error;

      if (images?.length > 0) {
        await Promise.all(images.map(async (img) => {
          if (!(img instanceof File)) return;

          const fileExt = img.name.split('.').pop();
          const fileName = `${Math.random()}.${fileExt}`;
          
          const { error: uploadError } = await supabase.storage
            .from('document')
            .upload(fileName, img);

          if (uploadError) throw uploadError;

          await supabase.from('image').insert({
            user_id: session.user.id,
            category: 'achievement',
            category_id: id,
            path: fileName
          });
        }));
      }

      await fetchAchievements();
    } catch (err) {
      console.error('Error updating achievement:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteAchievement = async (id) => {
    try {
      setLoading(true);
      
      // Delete associated images first
      const { data: images } = await supabase
        .from('image')
        .select('path')
        .eq('category_id', id);

      if (images?.length > 0) {
        // Delete from storage
        await supabase.storage
          .from('document')
          .remove(images.map(img => img.path));

        // Delete from database
        await supabase
          .from('image')
          .delete()
          .eq('category_id', id);
      }

      // Delete achievement
      const { error } = await supabase
        .from('achievement')
        .delete()
        .eq('id', id);

      if (error) throw error;

      await fetchAchievements();
    } catch (err) {
      console.error('Error deleting achievement:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteImage = async (imagePath) => {
    try {
      // Delete from storage
      await supabase.storage
        .from('document')
        .remove([imagePath]);

      // Delete from database
      await supabase
        .from('image')
        .delete()
        .eq('path', imagePath);

      await fetchAchievements();
    } catch (err) {
      console.error('Error deleting image:', err);
      throw err;
    }
  };

  useEffect(() => {
    if (session) {
      fetchAchievements();
    }
  }, [session]);

  return {
    achievements,
    loading,
    error,
    addAchievement,
    updateAchievement,
    deleteAchievement,
    deleteImage,
    fetchAchievements,
  };
}